import { useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import { Table } from '@/core/Table.atom';
import {
  COMPARISON_BASELINE_VALUE,
  COMPARISON_HEADER,
  COMPARISON_NO_VALUE,
  COMPARISON_TABLE_PROPS,
  SUMMARY_REPORT_TABLE_HEADERS,
  SUMMARY_REPORT_TABLE_PROPS,
} from '@/summaryReport/summaryData.constants';
import { getPropertyItems } from '@/summaryReport/summaryReport.utilities';

export interface SummaryReportTableProps {
  items: StatisticsSummaryItem[];
  baseline?: StatisticsSummaryItem | null;
  title?: string;
  visibleStats?: string[];
  isOverlay?: boolean;
  isCompareMode: boolean;
}

export const SummaryReportTable: React.FunctionComponent<SummaryReportTableProps> = ({
  items,
  baseline,
  isCompareMode = false,
  visibleStats = SUMMARY_REPORT_TABLE_PROPS.map((prop) => prop.key),
  isOverlay,
}) => {
  const { t } = useTranslation();
  const tableProps = isCompareMode
    ? [...SUMMARY_REPORT_TABLE_PROPS, ...COMPARISON_TABLE_PROPS]
    : SUMMARY_REPORT_TABLE_PROPS;

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initialExpandedState: Record<string, boolean> = {};
    SUMMARY_REPORT_TABLE_HEADERS.forEach((group) => (initialExpandedState[group] = true));
    return initialExpandedState;
  });
  const toggleGroup = (groupTitle: string) =>
    setExpandedGroups((prevExpandedState) => ({
      ...prevExpandedState,
      [groupTitle]: !prevExpandedState[groupTitle],
    }));

  const columns = [
    {
      accessor: 'statistic',
      cellStyle: {
        maxWidth: '70px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        alignItems: 'left' as const,
      },
      header: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STATISTIC',
    },
    ...items.map((item) => ({
      accessor: item.name,
      cellStyle: {
        maxWidth: '70px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'right' as const,
      },
      header: !isOverlay ? 'INVESTIGATE_TOOLS.SUMMARY_REPORT.VALUE' : item.name,
      cellRenderFunction: (rowItem: any) => {
        const value = rowItem[item.name];
        const renderText = value === COMPARISON_BASELINE_VALUE || value === COMPARISON_NO_VALUE;
        return <span className={clsx({ 'text-inactive': renderText })}>{renderText ? t(value) : value}</span>;
      },
    })),
  ];

  const renderStatisticsGroup = (groupTitle: string) => {
    const groupRows = tableProps
      .filter((prop) => prop.group === groupTitle && visibleStats.includes(prop.key))
      .map((prop) => {
        const row = getPropertyItems(prop.key, items, baseline?.id, prop.group === COMPARISON_HEADER);
        return { ...row, statistic: t(prop.label) };
      });
    // Remove group header if all stats are hidden by the dropdown
    if (groupRows.length === 0) return null;

    return (
      <div key={groupTitle} className="mb10">
        <div
          className="optionHeader mb5"
          role="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => toggleGroup(groupTitle)}>
          <span>{expandedGroups[groupTitle] ? '▾ ' : '▸ '}</span>
          <span>{t(groupTitle)}</span>
        </div>
        {expandedGroups[groupTitle] && (
          <div>
            <Table
              columns={columns}
              items={groupRows as any}
              headerClass="summaryReportTable"
              tableClass="summaryReportTable"
            />
          </div>
        )}
      </div>
    );
  };

  const groupTables = SUMMARY_REPORT_TABLE_HEADERS.map(renderStatisticsGroup).filter(Boolean);
  const headerRow =
    groupTables.length > 0 ? (
      <Table
        columns={columns}
        items={[] as any}
        headerClass="summaryReportTableHeader"
        tableClass="summaryReportTableHeader"
      />
    ) : null;

  return (
    <div>
      {headerRow}
      {groupTables}
    </div>
  );
};
